import React, { Suspense, useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { lazyPrefetching } from 'packages/helpers/helpers';
import usePrevious from 'packages/hooks/usePrevious';
import DesignsContainer from 'packages/pages/designs';
import SharedWithMeContainer from 'packages/pages/sharedWithMe';
import { LoadingBarProvider, LoadingBar } from 'packages/common/LoadingBar';
import ChunkErrorBoundary from 'packages/common/ChunkErrorBoundary';
import permissions from 'packages/configs/permissions';
import AuthorizationHelper from 'packages/helpers/AuthorizationHelper';
import features from 'packages/configs/features';
import { SimplePlayer } from 'packages/common/editor/contentSelection/PortionSelector';
import { AudioPlayer, VideoPlayer } from 'packages/common/player/Player';
import CreativeEditorContentLoader from 'packages/pages/app/creative/editor/CreativeEditorContentLoader';
import GenerateContentLoader from 'packages/pages/app/creative/editor/GenerateContentLoader';
import DesignEditor from 'packages/pages/app/creative/editor/DesignEditor';
import Generate from 'packages/pages/app/creative/editor/Generate';
import TemplateEditor from 'packages/pages/app/creative/editor/TemplateEditor';

const ProjectsContainer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "projects" */
            /* webpackPrefetch: true */
            'packages/pages/projects'
        ),
);

const ProjectDetailContainer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "project-detail" */
            /* webpackPrefetch: true */
            'packages/pages/projectDetail'
        ),
);

const BrandColorsContainer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "brand-colors" */
            /* webpackPrefetch: true */
            'packages/pages/brandColors'
        ),
);

const BrandPalettesContainer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "brand-palette" */
            /* webpackPrefetch: true */
            'packages/pages/brandPalettes'
        ),
);

const CategoriesContainer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "categories" */
            /* webpackPrefetch: true */
            'packages/pages/categories'
        ),
);

const CategoryDetailsContainer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "category-details" */
            /* webpackPrefetch: true */
            'packages/pages/categoryDetails'
        ),
);

const UploadsContainer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "uploads" */
            /* webpackPrefetch: true */
            'packages/pages/uploads'
        ),
);

const UploadCollectionsContainer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "uploadCollections" */
            /* webpackPrefetch: true */
            'packages/pages/uploadCollections'
        ),
);

const CollectionUploadsContainer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "uploadCollectionDetails" */
            /* webpackPrefetch: true */
            'packages/pages/uploadCollectionDetails'
        ),
);

const ActivityFeedContainer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "activityFeedContainer" */
            /* webpackPrefetch: true */
            'packages/pages/activity'
        ),
);

const ExportSettingsContainer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "exportSettings" */
            /* webpackPrefetch: true */
            'packages/pages/exportSettings/ExportSettings'
        ),
);

const NotFound = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "notFoundPage" */
            /* webpackPrefetch: true */
            'packages/pages/errors/not-found'
        ),
);

const NoAccess = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "noAccessPage" */
            /* webpackPrefetch: true */
            'packages/pages/errors/no-access'
        ),
);

const Body = () => {
    const [animate, setAnimate] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const prevLocation = usePrevious(location);

    const doAnimate = () => {
        setAnimate(false);

        setTimeout(() => {
            setAnimate(true);
        }, 100);
    };

    useEffect(() => {
        doAnimate();
        ProjectsContainer.prefetch();
        ProjectDetailContainer.prefetch();
        CategoriesContainer.prefetch();
        CategoryDetailsContainer.prefetch();
        BrandColorsContainer.prefetch();
        BrandPalettesContainer.prefetch();
        UploadsContainer.prefetch();
        UploadCollectionsContainer.prefetch();
        CollectionUploadsContainer.prefetch();
        SimplePlayer.prefetch();
        VideoPlayer.prefetch();
        AudioPlayer.prefetch();
    }, []);

    useEffect(() => {
        if (prevLocation && prevLocation.pathname !== location.pathname) {
            doAnimate();
        }

        if (location.pathname === '/') {
            navigate('/designs');
        }
    }, [location]);

    return (
        <div
            className={`h-100 ${animate ? 'transition-opacity' : ''}`}
            style={{
                opacity: animate ? 1 : 0,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                maxWidth: location.pathname === '/activity' ? 'calc(100% - 272px)' : '100%',
            }}
        >
            <ChunkErrorBoundary>
                <Suspense fallback={null}>
                    <Routes>
                        {/* @ts-expect-error TODO(VBS-25739) `exact` is a no-op in react-router v6 */}
                        <Route exact path="/designs" element={<DesignsContainer />} />
                        {AuthorizationHelper.hasPermission(permissions.CREATIVES_SPECIFICATIONS.READ) && (
                            <Route>
                                <Route path="/designs/new/edit" element={<CreativeEditorContentLoader />} />
                                <Route path="/designs/new" element={<GenerateContentLoader />} />
                                <Route
                                    path="/designs/:creativeId/edit"
                                    element={
                                        <LoadingBarProvider>
                                            <LoadingBar />
                                            <DesignEditor />
                                        </LoadingBarProvider>
                                    }
                                />
                                <Route
                                    path="/designs/:creativeId"
                                    element={
                                        <LoadingBarProvider>
                                            <LoadingBar />
                                            <Generate />
                                        </LoadingBarProvider>
                                    }
                                />
                            </Route>
                        )}
                        <Route path="/shared" element={<SharedWithMeContainer />} />
                        {/* @ts-expect-error TODO(VBS-25739) `exact` is a no-op in react-router v6 */}
                        <Route path="/projects" exact element={<ProjectsContainer />} />
                        <Route path="/projects/:projectId" element={<ProjectDetailContainer />} />
                        {AuthorizationHelper.hasPermission(permissions.ASSETS.READ) &&
                            AuthorizationHelper.isFeatureAvailable(features.INTERNAL_ASSET) && (
                                <Route>
                                    <Route path="/uploads" element={<UploadsContainer />} />
                                    {/* @ts-expect-error TODO(VBS-25739) `exact` is a no-op in react-router v6 */}
                                    <Route path="/collections" exact element={<UploadCollectionsContainer />} />
                                    <Route path="/collections/:collectionId" element={<CollectionUploadsContainer />} />
                                </Route>
                            )}
                        {/* @ts-expect-error TODO(VBS-25739) `exact` is a no-op in react-router v6 */}
                        <Route path="/brand-colors" exact element={<BrandPalettesContainer />} />
                        <Route path="/brand-colors/:paletteId" element={<BrandColorsContainer />} />
                        {/* @ts-expect-error TODO(VBS-25739) `exact` is a no-op in react-router v6 */}
                        <Route path="/categories" exact element={<CategoriesContainer />} />
                        <Route path="/categories/:categoryId" element={<CategoryDetailsContainer />} />
                        {AuthorizationHelper.hasPermission(permissions.TEMPLATES.WRITE) && (
                            <Route
                                path="/templates/:creativeId/edit"
                                element={
                                    <LoadingBarProvider>
                                        <LoadingBar />
                                        <TemplateEditor />
                                    </LoadingBarProvider>
                                }
                            />
                        )}
                        {/* @ts-expect-error TODO(VBS-25739) `exact` is a no-op in react-router v6 */}
                        <Route path="/activity" exact element={<ActivityFeedContainer />} />
                        {AuthorizationHelper.isFeatureAvailable(features.EXPORT_SETTINGS_ADMIN) && (
                            <Route path="/export-settings" element={<ExportSettingsContainer />} />
                        )}
                        <Route path="/not-found" element={<NotFound />} />
                        <Route path="/no-access" element={<NoAccess />} />
                        {/* <Redirect path="/templates" to="/categories" />
                        <Redirect path="/templates/:creativeId/" to="/categories" />
                        <Redirect path="/" to={AuthorizationHelper.getFallbackUrl()} /> */}
                        {/* <Redirect to="/not-found" /> uncomment if you want more strict url routing */}
                    </Routes>
                </Suspense>
            </ChunkErrorBoundary>
        </div>
    );
};

function mapStateToProps(state: any, ownProps: any) {
    const { loggingIn, companies, currentCompany } = state.user;

    return {
        loggingIn,
        companies,
        currentCompany,
        ...ownProps,
    };
}

export default connect(mapStateToProps)(Body);
